import type { ParsedUrlQuery } from 'querystring'
import { useRouter } from 'next/router'
// @mui
import { Container, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
// hooks
import useSettings from '@/hooks/useSettings'
import useLocales from '@/hooks/useLocales'
import useApp from '@/hooks/useApp'

// layouts
import Layout from '@/layouts/index'
// components
import Page from '@/components/Page'
// sections
import { settingService, GlobalSetting, SeoMetadata } from '@/services/page'
import pathService from '@/services/path'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import { notEmpty } from '@/utils/common'
import flatten from 'lodash/flatten'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { glossaryService, extractTag } from '@/services/glossary'
import uniqBy from 'lodash/uniqBy'
import { renderOptionFunc, RichTextStyleWrapper } from '@/components/contentful/Shared'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

interface PathProps extends ParsedUrlQuery {
  appId: string
  slug: string[]
}

interface TagsPageProps {
  preview: boolean
  globalSetting?: GlobalSetting
  tag: ReturnType<typeof extractTag>
  seoMetadataItemsCollection?: SeoMetadata[]
}

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
  },
}))

// ----------------------------------------------------------------------

Tags.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

export default function Tags({ tag, globalSetting, seoMetadataItemsCollection }: TagsPageProps) {
  const { themeStretch } = useSettings()
  const { locale } = useRouter()
  const { host } = useApp()
  const { translate } = useLocales()

  return (
    <Page
      title={tag?.name ?? ''}
      description={
        tag?.content?.json ? documentToPlainTextString(tag.content?.json).slice(250) : undefined
      }
      metadataItems={seoMetadataItemsCollection}
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'DefinedTerm',
        name: tag?.name ?? '',
        description: tag?.content?.json
          ? documentToPlainTextString(tag.content?.json).slice(250)
          : undefined,
        url: `https://${host}/${locale}/glossary/${tag?.slug ?? ''}}`,
        inDefinedTermSet: {
          '@type': 'DefinedTermSet',
          name: translate('common.glossary'),
          url: `https://${host}/${locale}/glossary`,
        },
      }}
    >
      <RootStyle>
        <Container maxWidth={themeStretch ? false : 'lg'}>
          <Typography textAlign="center" variant="h2" my={5}>
            {tag?.name}
          </Typography>
          <div className="max-w-2xl mx-auto">
            <RichTextStyleWrapper>
              {tag?.content &&
                documentToReactComponents(
                  tag.content.json,
                  renderOptionFunc(tag.content.links?.assets)
                )}
            </RichTextStyleWrapper>
          </div>
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getGlossaryPaths } = pathService()

  const allAppTagPaths = await Promise.allSettled(
    APPS.map(async (app) => getGlossaryPaths(app.id))
  ).then((res) => res.map((r) => (r.status === 'fulfilled' ? r.value : null)).filter(notEmpty))

  const allTagPaths = uniqBy(
    flatten(allAppTagPaths).filter(({ params: { slug, appId } }) => slug.join('')),
    (item) => item.params.slug.join('') + item.params.appId + item.locale
  )
  return {
    paths: allTagPaths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<TagsPageProps, PathProps> = async ({
  preview = false,
  params,
  locale,
}) => {
  if (!params) throw new Error('No path parameters found')
  const { appId } = params
  let tagId: string | null = null
  let tag: ReturnType<typeof extractTag> = null
  let seoMetadataItemsCollection: SeoMetadata[] = []
  const { getTagIdBySlug, getTagByTagId } = glossaryService(appId)
  const { getGlobalSetting } = settingService(appId)
  const globalSetting = await getGlobalSetting({ locale })
  tagId = await getTagIdBySlug(params.slug.join('/'), { locale })
  if (tagId) {
    const data = await getTagByTagId(tagId, { locale })
    tag = data.tag
    seoMetadataItemsCollection = data.seoMetadataItemsCollection
  }
  return {
    props: {
      preview,
      globalSetting,
      tag,
      seoMetadataItemsCollection,
    },
    revalidate: 3600,
  }
}
